import React, { useState } from 'react';
import { API_BASE_URL } from '../config';
import './DatadogDemo.css';

function DatadogDemo() {
  // Profiler Demo State
  const [movieId, setMovieId] = useState('1');
  const [seatCount, setSeatCount] = useState('5');
  const [preferCenter, setPreferCenter] = useState(true);
  const [profilerResult, setProfilerResult] = useState(null);
  const [profilerLoading, setProfilerLoading] = useState(false);

  // Live Debugger Demo State
  const [priceSeatId, setPriceSeatId] = useState('1');
  const [showTime, setShowTime] = useState('19:00');
  const [promoCode, setPromoCode] = useState('');
  const [seatGrade, setSeatGrade] = useState('STANDARD');
  const [priceResult, setPriceResult] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);

  // Profiler Demo - 좌석 추천
  const handleProfilerDemo = async () => {
    setProfilerLoading(true);
    setProfilerResult(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/apm-demo/seats/recommend?movieId=${movieId}&count=${seatCount}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            preferCenter,
            preferFront: false,
            preferAisle: false,
            maxPrice: 50000,
            preferredGrade: 'STANDARD'
          })
        }
      );
      const data = await response.json();
      setProfilerResult(data);
    } catch (error) {
      setProfilerResult({ error: error.message });
    }
    setProfilerLoading(false);
  };

  // Live Debugger Demo - 가격 계산
  const handlePriceDemo = async () => {
    setPriceLoading(true);
    setPriceResult(null);
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await fetch(`${API_BASE_URL}/apm-demo/pricing/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seatId: parseInt(priceSeatId),
          showTime: `${today}T${showTime}:00`,
          promotionCode: promoCode || null,
          seatGrade
        })
      });
      const data = await response.json();
      setPriceResult(data);
    } catch (error) {
      setPriceResult({ error: error.message });
    }
    setPriceLoading(false);
  };

  return (
    <div className="datadog-demo">
      <h1>🐕 Datadog APM Demo</h1>
      <p className="subtitle">Live Debugger, Continuous Profiler 테스트</p>
      


      {/* Profiler Demo */}
      <section className="demo-section profiler">
        <h2>📊 Continuous Profiler Demo</h2>
        <p className="description">O(n²) 알고리즘으로 CPU 핫스팟 생성 - Datadog Profiler에서 확인</p>
        
        <div className="input-group">
          <label>
            Movie ID:
            <input type="number" value={movieId} onChange={(e) => setMovieId(e.target.value)} min="1" />
          </label>
          <label>
            추천 좌석 수:
            <input type="number" value={seatCount} onChange={(e) => setSeatCount(e.target.value)} min="1" max="50" />
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={preferCenter} onChange={(e) => setPreferCenter(e.target.checked)} />
            중앙 좌석 선호
          </label>
        </div>
        
        <button onClick={handleProfilerDemo} disabled={profilerLoading} className="btn-profiler">
          {profilerLoading ? '처리 중...' : '🚀 좌석 추천 실행'}
        </button>
        
        {profilerResult && (
          <div className="result">
            <h4>결과 (처리시간: {profilerResult.processingTimeMs}ms)</h4>
            {profilerResult.error ? (
              <p className="error">{profilerResult.error}</p>
            ) : (
              <div>
                <p>분석된 좌석 수: {profilerResult.totalSeatsAnalyzed}</p>
                <p>추천 좌석 수: {profilerResult.recommendedSeats?.length || 0}</p>
                <table>
                  <thead>
                    <tr><th>좌석</th><th>점수</th><th>등급</th><th>가격</th></tr>
                  </thead>
                  <tbody>
                    {profilerResult.recommendedSeats?.map((seat, i) => (
                      <tr key={i}>
                        <td><span className="data-highlight">{seat.seatNumber}</span></td>
                        <td><span className="data-score">{seat.score?.toFixed(2)}</span></td>
                        <td><span className="data-metric">{seat.grade}</span></td>
                        <td><span className="data-currency">₩{seat.price?.toLocaleString()}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Live Debugger Demo */}
      <section className="demo-section live-debugger">
        <h2>🐛 Live Debugger Demo</h2>
        <p className="description">가격 계산 중간값 캡처 - Datadog Live Debugger에서 변수 값 확인</p>
        
        <div className="input-group">
          <label>
            Seat ID:
            <input type="number" value={priceSeatId} onChange={(e) => setPriceSeatId(e.target.value)} min="1" />
          </label>
          <label>
            상영 시간:
            <select value={showTime} onChange={(e) => setShowTime(e.target.value)}>
              <option value="08:00">08:00 (조조 -20%)</option>
              <option value="12:00">12:00 (일반)</option>
              <option value="19:00">19:00 (피크 +20%)</option>
              <option value="23:00">23:00 (심야 -20%)</option>
            </select>
          </label>
          <label>
            좌석 등급:
            <select value={seatGrade} onChange={(e) => setSeatGrade(e.target.value)}>
              <option value="STANDARD">일반석 (x1.0)</option>
              <option value="PREMIUM">프리미엄 (x1.3)</option>
              <option value="VIP">VIP (x1.5)</option>
              <option value="COUPLE">커플석 (x1.8)</option>
            </select>
          </label>
          <label>
            프로모션 코드:
            <select value={promoCode} onChange={(e) => setPromoCode(e.target.value)}>
              <option value="">없음</option>
              <option value="WELCOME10">WELCOME10 (-10%)</option>
              <option value="VIP20">VIP20 (-20%)</option>
              <option value="STUDENT30">STUDENT30 (-30%)</option>
            </select>
          </label>
        </div>
        
        <button onClick={handlePriceDemo} disabled={priceLoading} className="btn-debugger">
          {priceLoading ? '계산 중...' : '🐛 가격 계산 실행'}
        </button>
        
        {priceResult && (
          <div className="result">
            <h4>가격 계산 결과</h4>
            {priceResult.error ? (
              <p className="error">{priceResult.error}</p>
            ) : (
              <div className="price-breakdown">
                <div className="price-final">
                  <span>
                    <span className="currency-symbol">₩</span>
                    <span className="amount">{priceResult.finalPrice?.toLocaleString()}</span>
                  </span>
                </div>
                {priceResult.adjustments && priceResult.adjustments.length > 0 && (
                  <div className="adjustments">
                    <strong>적용된 조정</strong>
                    <ul>
                      {priceResult.adjustments.map((adj, i) => (
                        <li key={i} className={adj.includes('할인') ? 'discount-highlight' : adj.includes('프리미엄') || adj.includes('VIP') ? 'premium-highlight' : ''}>{adj}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default DatadogDemo;
